import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthContext';
import { productionLogger } from '@/utils/production-logger';
import type { UserRole } from '@/types/user.types';

interface UserRoleData {
  role: UserRole;
}

export const useUserRole = () => {
  const { user, loading: authLoading } = useAuth();

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['user-roles', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (error) {
        productionLogger.error('Error fetching user roles:', error);
        return [];
      }

      return data?.map((item: UserRoleData) => item.role) || [];
    },
    enabled: !!user?.id && !authLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const hasRole = (role: UserRole): boolean => {
    return roles.includes(role);
  };

  const isAdmin = (): boolean => {
    return hasRole('admin');
  };

  return {
    roles,
    loading: isLoading || authLoading,
    hasRole,
    isAdmin,
  };
};